import { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useJSONModal } from 'modules/Predictions/components/PredictionSidebar/useUploadModal';
import { useMediaPlayback } from 'modules/Canvas/contexts/MediaProvider/MediaPlayback';
import { SearchInput } from 'components/ui_library/SearchInput';
import { Button } from 'components/ui_library/Button';
import { IconTagMultiSelect } from 'components/Icons';
import { CollapsibleBar, CollapsibleBarProps } from '../CollapsibleBar/CollapsibleBar';
import { hasConcepts, getFormattedValue, hasFrames } from '../utils';

interface ConceptWithoutRegionViewProps {
  model: CF.API.Models.Model;
  modelOutput: CF.API.ModelOutputs.ModelOutput;
  collapsibleBarProps: Omit<CollapsibleBarProps, 'children' | 'isThresholdSliderVisible'>;
}

export const ConceptWithoutRegionView = observer(({ model, modelOutput, collapsibleBarProps }: ConceptWithoutRegionViewProps): JSX.Element => {
  const { data } = modelOutput;
  const minThreshold = collapsibleBarProps.minThreshold || 0;
  const mediaPlayback = useMediaPlayback();
  const currentSecond = mediaPlayback.currentTimeInSeconds;

  const conceptData = useMemo(() => {
    if (hasFrames(data)) {
      const frameData = data.frames && data.frames?.[currentSecond]?.data;
      return frameData || data;
    } else {
      return data;
    }
  }, [data, currentSecond]);

  const [filterString, setFilterString] = useState<string>(''); // Display Filter Concept only if output has concepts

  const onFilterStringChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFilterString(e.target.value);
  };

  const openJsonModal = useJSONModal(
    {
      json: modelOutput,
    },
    'model_page',
  );

  return (
    <CollapsibleBar modelId={model.id} isThresholdSliderVisible {...collapsibleBarProps}>
      <div className="filter-bar">
        <SearchInput placeholder="Filter by concept" onChange={onFilterStringChange} value={filterString} />
      </div>
      <div className="concepts">
        {hasConcepts(conceptData) &&
          conceptData.concepts
            ?.filter((concept) => concept.name.toLowerCase().includes(filterString.toLowerCase()))
            .map((concept) => {
              const conceptValue = getFormattedValue(concept.value || 0);

              if (Number.parseFloat(conceptValue) < minThreshold) {
                return null;
              }

              return (
                <div className="concept">
                  <div className="concept-name">
                    <IconTagMultiSelect size={19} color="#344054" />
                    {concept.name}
                  </div>
                  <div className="concept-value">{conceptValue && conceptValue}</div>
                </div>
              );
            })}
      </div>
      <Button variant="secondary" size="sm" onClick={openJsonModal}>
        View JSON
      </Button>
    </CollapsibleBar>
  );
});
